import { api_fetch } from "./client.tsx"

export type SplitWizPath = "success" | "no_backup" | "error"

export interface SplitWizPreviewLine {
  amount: number
  customer_name?: string | null
  retailer_name?: string | null
  product_line?: string | null
  product_category?: string | null
  execution_date?: string | null
  promo_type?: string | null
  promo_sub_type?: string | null
  code_description?: string | null
  retailer_invoice_number?: string | null
  /** Ledger / GL reason code id when the org has a chart of accounts */
  reason_code_id?: string | null
  account_number?: string | null
  expense_account?: string | null
}

export interface SplitWizOutcome {
  deduction_id: string
  path: SplitWizPath
  success: boolean
  error?: string | null
  summary_bullets: string[]
  summary_markdown: string
  splits_created: number
  model_reasoning?: string | null
  field_mapping_notes: string[]
  proposed_split_preview: SplitWizPreviewLine[]
}

export interface RunSplitWizardOptions {
  feedback?: string
}

export interface SplitWizardBulkResponse {
  results: SplitWizOutcome[]
}

export interface BulkSplitWizardOptions {
  feedback?: string
}

export async function bulk_split_wizard(deduction_ids: string[], options?: BulkSplitWizardOptions) {
  return api_fetch<SplitWizardBulkResponse>("/accounting/split-wiz/bulk", {
    method: "POST",
    body: {
      deduction_ids,
      feedback: options?.feedback?.trim() || null,
    },
  })
}

export async function run_split_wizard(deduction_id: string, options?: RunSplitWizardOptions) {
  return api_fetch<SplitWizOutcome>("/accounting/split-wiz/run", {
    method: "POST",
    body: {
      deduction_id,
      feedback: options?.feedback?.trim() || null,
    },
  })
}

export type SplitWizardStreamEvent =
  | { type: "progress"; message: string }
  | { type: "thinking_delta"; content: string }
  | { type: "thinking_done" }
  | { type: "outcome"; outcome: SplitWizOutcome }
  | { type: "error"; error: string }

export interface SplitWizardStreamCallbacks {
  on_progress?: (message: string) => void
  on_thinking_delta?: (content: string) => void
  on_thinking_done?: () => void
  on_outcome: (outcome: SplitWizOutcome) => void
  on_error: (message: string) => void
}

function parse_split_wizard_stream_event(raw: unknown): SplitWizardStreamEvent | null {
  if (!raw || typeof raw !== "object") {
    return null
  }
  const o = raw as Record<string, unknown>
  const t = o.type
  if (t === "progress" && typeof o.message === "string") {
    return { type: "progress", message: o.message }
  }
  if (t === "thinking_delta" && typeof o.content === "string") {
    return { type: "thinking_delta", content: o.content }
  }
  if (t === "thinking_done") {
    return { type: "thinking_done" }
  }
  if (t === "outcome" && o.outcome && typeof o.outcome === "object") {
    return { type: "outcome", outcome: o.outcome as SplitWizOutcome }
  }
  if (t === "error" && typeof o.error === "string") {
    return { type: "error", error: o.error }
  }
  return null
}

export async function run_split_wizard_stream(
  deduction_id: string,
  callbacks: SplitWizardStreamCallbacks,
  options?: RunSplitWizardOptions,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch("/api/accounting/split-wiz/run-stream", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    credentials: "same-origin",
    body: JSON.stringify({
      deduction_id,
      feedback: options?.feedback?.trim() || null,
    }),
    signal,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => "")
    callbacks.on_error(text || `Request failed (${response.status})`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.on_error("No response body")
    return
  }

  const decoder = new TextDecoder()
  let buffer = ""

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop() || ""

    for (const line of lines) {
      if (!line.startsWith("data: ")) {
        continue
      }
      const data = line.slice(6).trim()
      if (!data || data === "keep-alive") {
        continue
      }
      try {
        const raw: unknown = JSON.parse(data)
        const ev = parse_split_wizard_stream_event(raw)
        if (!ev) {
          continue
        }
        switch (ev.type) {
          case "progress":
            callbacks.on_progress?.(ev.message)
            break
          case "thinking_delta":
            callbacks.on_thinking_delta?.(ev.content)
            break
          case "thinking_done":
            callbacks.on_thinking_done?.()
            break
          case "outcome":
            callbacks.on_outcome(ev.outcome)
            break
          case "error":
            callbacks.on_error(ev.error)
            break
        }
      } catch {
        // skip malformed JSON
      }
    }
  }
}
